import React, { useEffect, useState } from 'react';
import NotFoundPage from './pages/NotFound/NotFound';
import ForbiddenPage from './pages/Forbidden/Forbidden';
import { Redirect, Switch, Route, useRouteMatch } from 'react-router-dom';
import LoginPage from './pages/LoginPage/LoginPage';

import { IIconRoot } from '@scuf/common/dist/types';
import { ThemeType } from '@scuf/common';

import { AuthenticatedUser } from './auth/models';

import { UserList } from './modules/onboarding/UserManagement/UserList';

// import { OnboardingFeature } from '@bmsp/bmsp.package.web.onboarding';
import { OnboardingFeature } from './modules/onboarding/OnboardingFeature';
import { DashboardFeature } from './modules/onboarding/DashboardFeature';

import HierarchyBuilder from './modules/onboarding/OrganisationManagement/HierarchyBuilder/HierarchyBuilder';

import Playground from './playground';
import { Loader } from '@scuf/common';
import { APIProvider } from './modules/commonUtils/APIContext';
import TemplateList from './modules/onboarding/TemplateManagement/TemplateList';
import SKUList from './modules/onboarding/PartnerManagement/SKULIst/SKUList';
import GatewayManagement from './modules/onboarding/GatewayManagement';
import AssetModelling from './modules/onboarding/AssetModellingManagement/AssetModelling';
import SubscriptionManagementNew from './modules/onboarding/SubscriptionManagementNew/SubscriptionManagementNew';
import { ReactComponent as SubscriptionIcon } from './assets/subscription-menu-icon.svg';
import { ReactComponent as UserManagementNewIcon } from './assets/user-management-new.svg';
import UserManagementNew from './modules/onboarding/UserManagementNew/UserManagementNew';
import OrganisationManagementNew from './modules/onboarding/OrganisationManagementNew/OrganisationManagementNew';
import { HomeIcon, RecentIcon } from '@forge/common';
import { ReactComponent as GatewayIcon } from './assets/gatewayicon.svg';
import { ReactComponent as IntegrationIcon } from './assets/integrations.svg';
import { ReactComponent as LicenseIcon } from './assets/licenseicon.svg';
import { ReactComponent as ProjectManagementIcon } from './assets/projectmanagement.svg';
import Support from './pages/Support/Support';
import IntegrationMicroFrontendPage from './remoteComponents/Integration/IntegrationMicroFrontendPage';
import BmsAssistsMicroFrontendPage from './remoteComponents/BMSAssist/BmsAssistsMicroFrontendPage';
import LicenseManagement from './modules/onboarding/LicenseManagement/LicenseManagement';
import { useAuthService } from './auth/hooks/useAuthService';
import { ActivityLandingPage } from './modules/activity';

export interface IRoute {
  path: string;
  name?: string;
  icon?: ISidebarIconProps;
  exact?: boolean;
  component: React.ReactNode;
  children?: Array<IRoute>;
  authRequired?: boolean;
  // Routes sharing a routeKey keep one mounted instance when navigating between them.
  routeKey?: string;
}

interface ISidebarIconProps {
  root: IIconRoot;
  icon: string | React.ReactElement;
}

/**
 * Currently just checks if the user exists or not
 * Should actually check against priviledges, but those don't exist yet
 */
const authGuard = (route: IRoute, user: AuthenticatedUser | null) => {
  return !route.authRequired || (route.authRequired && user !== null);
};

export const LoginRedirectPage = () => {
  const { user, isAuthenticated, signIn, isLoading } = useAuthService();
  const [userAlreadyLoggedIn, setUserAlreadyLoggedIn] = useState<boolean>(
    false
  );
  
  useEffect(() => {
    if (!isLoading) {
      if (isAuthenticated && user) {
        setUserAlreadyLoggedIn(true);
      } else if (!isAuthenticated) {
        setUserAlreadyLoggedIn(true);
      } else {
        signIn();
      }
    }
  }, [isLoading, isAuthenticated, user, signIn]);
  
  return (
    <div>
      {userAlreadyLoggedIn ? <Redirect to={'/'} /> : null}
      <Loader text={'Redirecting to login page'} overlayOpacity={0} />
    </div>
  );
};

/**
 * Routing table:
 * Contains all known routes by the app, not including feature routes which should be handled by the features
 * Will render a sidebar element if the "name" field is populated
 */
export const SITE_ROUTE_PATH =
  '/app/feature/organisations/:rootId/customers/:custId/sites/:siteId';

const routes = (
  key: boolean,
  isContainSiOrg: boolean,
  isGatewayManagement: boolean,
  isEnableBacsFlow: boolean,
  isLandingPageDashboard: boolean,
  loggedInUserDetails: AuthenticatedUser | null,
  enableSupportIcon: boolean,
  isDistributor: boolean,
  isCustomer: boolean,
  isEnableIntegrationMenu: boolean,
  isEnableARRLicenseFlow: boolean,
  rootNodeId: string = '',
  rootNodeName: string = '',
  isBmsAssistEnabled: boolean = false,
  isBmsAssistProjectOsSubscriptionEnabled: boolean = false,
  isBmsAssistProjectOsSubscriptionEnabledByOrgId: boolean = false,
  isProjectManagementTabApiEnabled: boolean = false,
  canCreateUser: boolean = false,
  canEditUser: boolean = false,
  canDeleteUser: boolean = false,
  canCreateProject: boolean = false,
  canEditProject: boolean = false,
  canDeleteProject: boolean = false,
  canUpdateProjectStructure: boolean = false,
  canUpdateProjectDocument: boolean = false,
  canViewProjectDocument: boolean = false,
  highestRole: string = 'None',
  isEnableActivityLog: boolean = false,
  theme: ThemeType = ThemeType.Light,
  languageCode: string = 'en-US',
  baseURL: string,
  authFetch: (input: RequestInfo, init?: RequestInit) => Promise<Response>,
  offering: string,
  hasGlobalPermission: (permission: string) => boolean,
): IRoute[] => {
  let routesArray: IRoute[] = [
    {
      path: '/',
      exact: true,
      component: (
        <DashboardFeature isLandingPageDashboard={isLandingPageDashboard} />
      )
    },
    {
      path: '/feature/home',
      name: 'home',
      icon: {
        icon: (
          <HomeIcon
            style={{ width: '24px', height: '19px', margin: '0 0.5rem 0 -3px' }}
          />
        ),
        root: 'common'
      },
      authRequired: true,
      exact: true,
      component: (
        <DashboardFeature isLandingPageDashboard={isLandingPageDashboard} />
      )
    },
    {
      path: '/feature/organisations',
      name: isEnableBacsFlow
        ? isDistributor
          ? 'contractors'
          : 'customers'
        : 'organisations',
      icon: { icon: 'buildings', root: 'common' },
      authRequired: true,
      exact: true,
      routeKey: 'organisations',
      component: isEnableBacsFlow ? (
        <OrganisationManagementNew loggedInUser={loggedInUserDetails} />
      ) : (
        <OnboardingFeature />
      ),
      children: [
        {
          path: '/hierarchy-builder',
          authRequired: true,
          component: <HierarchyBuilder />
        }
      ]
    },
    {
      // routes here are relative to the /app mount
      path: SITE_ROUTE_PATH.replace(/^\/app/, ''),
      authRequired: true,
      exact: true,
      routeKey: 'organisations',
      component: isEnableBacsFlow ? (
        <OrganisationManagementNew loggedInUser={loggedInUserDetails} />
      ) : (
        <Redirect to="/app/feature/organisations" />
      )
    },
    {
      path: '/feature/users',
      name: 'users',
      icon: { icon: 'user', root: 'common' },
      authRequired: true,
      component: (
        <UserList
          baseURL={baseURL}
          authFetch={authFetch}
          offering={offering}
        />
      )
    }
  ];

  // Added condition based on the the new BACS flow and the feature flag. This menu enable only for if enableBACSFlow feature flag is off.
  if (!isEnableBacsFlow) {
    routesArray.push({
      path: '/feature/templates',
      name: 'templates',
      icon: { icon: 'template', root: 'common' },
      authRequired: true,
      component: (
        <TemplateList
          baseURL={baseURL}
          authFetch={authFetch}
          offering={offering}
        />
      )
    });
  }
  // isDistributor is used to hide the asset modelling menu for the BACS distributor flow
  if (isContainSiOrg && !isDistributor) {
    routesArray.push({
      path: '/feature/asset-modelling',
      name: 'assetModelling',
      icon: { icon: 'gateway-set', root: 'common' },
      authRequired: true,
      component: (
        <AssetModelling
          baseURL={baseURL}
          authFetch={authFetch}
          offering={offering}
        />
      )
    });
  }

  if (
    key &&
    !isEnableBacsFlow &&
    hasGlobalPermission('SubscriptionManagement_SKU_Read')
  ) {
    routesArray.push({
      path: '/feature/subscription',
      name: 'subscriptions',
      icon: { icon: 'shopping-bag', root: 'global' },
      authRequired: true,
      component: (
        <SKUList
          baseURL={baseURL}
          authFetch={authFetch}
          offering={offering}
        />
      )
    });
  }
  if (key && isEnableBacsFlow) {
    routesArray.push({
      path: '/feature/subscription',
      name: 'subscriptions',
      icon: {
        icon: (
          <SubscriptionIcon
            className="subscription-icon"
            style={{ width: '24px', height: '24px', margin: '0 0.5rem 0 -3px' }}
          />
        ),
        root: 'common'
      },
      authRequired: true,
      component: <SubscriptionManagementNew />
    });
  }

  if (isEnableBacsFlow) {
    const foundIndex = routesArray.findIndex(
      route => route.path === '/feature/users'
    );
    if (foundIndex !== -1) {
      routesArray.splice(foundIndex, 1, {
        path: '/feature/users',
        name: 'users',
        icon: {
          icon: (
            <UserManagementNewIcon
              className="user-mgmt-new-icon"
              style={{ width: '18px', height: '18px', margin: '0 0.9rem 0 0' }}
            />
          ),
          root: 'common'
        },
        authRequired: true,
        component: <UserManagementNew canCreateUser={canCreateUser} canEditUser={canEditUser} canDeleteUser={canDeleteUser} />
      });
    }
  }

  // Added condition based on the EnableARRLicenseFlow feature flag. This menu enable only if EnableARRLicenseFlow feature flag is ON.
  if (isEnableARRLicenseFlow) {
    routesArray.push({
      path: '/feature/licenses',
      name: 'licenseManagement',
      icon: {
        icon: (
          <LicenseIcon
            className="gateway-mgmt-new-icon"
            style={{ width: '22px', height: '22px', margin: '0 0.5rem 0 -3px' }}
          />
        ),
        root: 'common'
      },
      authRequired: true,
      component: <LicenseManagement loggedInUser={loggedInUserDetails}/>
    });
  }

  // isDistributor is used to hide the gateway management menu for the BACS distributor flow
  if (isGatewayManagement && isEnableBacsFlow && !isDistributor) {
    routesArray.push({
      path: '/feature/gateway-management',
      name: 'gatewayManagement',
      icon: {
        icon: (
          <GatewayIcon
            className="gateway-mgmt-new-icon"
            style={{ width: '22px', height: '22px', margin: '0 10px 0 0' }}
          />
        ),
        root: 'common'
      },
      authRequired: true,
      component: <GatewayManagement />
    });
  }

  //Remove all the routes it from the array  and home route is /feature/organisations and remove the /, home route if the user is a customer
  if (isCustomer) {
    // Set the initial route to /feature/organisations for customers logged in
    const initialRouteIndex = routesArray.findIndex(
      route => route.path === '/feature/home'
    );
    if (initialRouteIndex !== -1) {
      routesArray[initialRouteIndex] = {
        ...routesArray[initialRouteIndex],
        name: 'Sites',
        icon: { icon: 'buildings', root: 'common' },
        authRequired: true,
        component: (
          <OrganisationManagementNew loggedInUser={loggedInUserDetails} />
        )
      };
    }
    routesArray = routesArray.filter(route => {
      return ![
        '/',
        '/feature/templates',
        '/feature/organisations',
        '/feature/gateway-management',
        '/feature/asset-modelling'
      ].includes(route.path);
    });
  }
  // Ensure the subscription route exists for BACS flow, if not, add it
  const hasSubscriptionRoute = routesArray.some(
    route => route.path === '/feature/subscription'
  );
  if (!hasSubscriptionRoute) {
    routesArray.push({
      path: '/feature/subscription',
      name: 'subscriptions',
      icon: {
        icon: (
          <SubscriptionIcon
            className="subscription-icon"
            style={{ width: '24px', height: '24px', margin: '0 0.5rem 0 -3px' }}
          />
        ),
        root: 'common'
      },
      authRequired: true,
      component: <SubscriptionManagementNew />
    });
  }

  routesArray.push({
    path: '/feature/asset-modelling',
    name: '',
    component: (
      <AssetModelling
        baseURL={baseURL}
        authFetch={authFetch}
        offering={offering}
      />
    )
  });

  // Added condition based on the feature flag. This menu enable only for if enableIntegrationMenu feature flag is ON.
  if (isEnableIntegrationMenu) {
    routesArray.push({
      path: '/feature/integrations',
      name: 'integration',
      icon: {
        icon: (
          <IntegrationIcon
            className="gateway-mgmt-new-icon"
            style={{ width: '22px', height: '22px', margin: '0 10px 0 0' }}
          />
        ),
        root: 'common'
      },
      authRequired: true,
      component: (
        <IntegrationMicroFrontendPage
          customerId={''}
          userDetail={{
            userId: loggedInUserDetails?.name || loggedInUserDetails?.id || '',
            email:
              loggedInUserDetails?.email ||
              ''
          }}
        />
      )
    });
  }

  if (isBmsAssistEnabled || isBmsAssistProjectOsSubscriptionEnabled || isBmsAssistProjectOsSubscriptionEnabledByOrgId || isProjectManagementTabApiEnabled) {
  routesArray.push({
    path: '/feature/project-management',
    name: 'Project Management',
    icon: {
      icon: (
        <ProjectManagementIcon
          className="project-management-icon"
          style={{ width: '22px', height: '22px', margin: '0 10px 0 0' }}
        />
      ),
      root: 'common'
    },
    authRequired: true,
    component: (
      <BmsAssistsMicroFrontendPage
        customerId={''}
        userDetail={{
          id: loggedInUserDetails?.profile?.sub || '',
          email:
            loggedInUserDetails?.profile?.preferred_username ||
            loggedInUserDetails?.profile?.email ||
            ''
        }}
        // BMS Assists API base URL from config.js → window.ENV.BMS_ASSISTS_API_BASE_URL
        apiBaseUrl={(window as any).ENV.BMS_ASSISTS_API_BASE_URL}
        rootNodeId={rootNodeId}
        rootNodeName={rootNodeName}
        baseUrl={(window as any).ENV.BASE_URL}
        canCreateProject={canCreateProject}
        canEditProject={canEditProject}
        canDeleteProject={canDeleteProject}
        canUpdateProjectStructure={canUpdateProjectStructure}
        canUpdateProjectDocument={canUpdateProjectDocument}
        highestRole={highestRole}
        theme={theme}
        locale={languageCode}
        language={languageCode}
        languageCode={languageCode}
        imageSupport={{
          orgLogoWidth: (window as any).ENV.ORG_LOGO_IMG_WIDTH,
          orgLogoHeight: (window as any).ENV.ORG_LOGO_IMG_HEIGHT,
          orgLogoMinWidth: (window as any).ENV.ORG_LOGO_MIN_IMG_WIDTH,
          orgLogoMinHeight: (window as any).ENV.ORG_LOGO_MIN_IMG_HEIGHT,
          siteImageWidth: (window as any).ENV.SITE_IMG_WIDTH,
          siteImageHeight: (window as any).ENV.SITE_IMG_HEIGHT,
          maxImageSizeInKb: (window as any).ENV.MAX_IMG_SIZE_IN_KB,
          orgImageSupportedTypes: (window as any).ENV.ORG_IMG_SUPPORTED_TYPE,
          siteImageSupportedTypes: (window as any).ENV.SITE_IMG_SUPPORTED_TYPE
        }}
      />
    )
  });
}

  if (isEnableActivityLog) {
    routesArray.push({
      path: '/feature/activity-log',
      name: '',
      icon: {
        icon: (
          <RecentIcon
            className="gateway-mgmt-new-icon"
            style={{ width: '22px', height: '22px', margin: '0 10px 0 0' }}
          />
        ),
        root: 'common'
      },
      authRequired: true,
      component: <ActivityLandingPage />
    });
  }

  routesArray.push({
    path: '/feature/testing',
    name: '',
    component: <Playground />
  });
  routesArray.push({
    path: '/forbidden',
    name: '',
    component: <ForbiddenPage />
  });
  if (enableSupportIcon) {
    routesArray.push({
      path: '/support',
      name: '',
      component: <Support loggedInUser={loggedInUserDetails} />
    });
  }

  if (isContainSiOrg !== undefined) {
    routesArray.push({
      path: '/not-found',
      name: '',
      component: <NotFoundPage />
    });
    routesArray.push({
      path: '*',
      name: '',
      component: <Redirect to={'/not-found'} />
    });
  }

  routesArray.push({
    path: '/login-redirect',
    name: '',
    component: <LoginRedirectPage />
  });

  return routesArray;
};

interface ISwitchRoutingTableProps {
  routes: IRoute[];
  user: AuthenticatedUser | null;
}

/**
 * A component to generate the switch routing table for the app based on a predefined route array.
 */
export const SwitchRoutingTable = (props: ISwitchRoutingTableProps) => {
  const routeMatch = useRouteMatch();
  const { baseURL, authFetch, offering } = useAuthService();
  const apiPackage = {
    baseURL,
    authFetch,
    offering
  };

  return (
    <APIProvider APIPackage={apiPackage}>
      <Switch>
        {props.routes.map((route, index) => {
          return [
            // tslint:disable-next-line: jsx-wrap-multiline
            <Route
              key={route.routeKey ?? index + route.path}
              path={(routeMatch && routeMatch.path) + route.path}
              exact={route.exact}
              children={() => {
                if (authGuard(route, props.user)) {
                  return route.component;
                } else {
                  return (
                    <Redirect
                      to={(routeMatch && routeMatch.path) + '/forbidden'}
                    />
                  );
                }
              }}
            />,
            route.children
              ? route.children.map((childroute, childIndex) => {
                  return (
                    <Route
                      key={childIndex + childroute.path}
                      path={
                        (routeMatch && routeMatch.path) +
                        route.path +
                        childroute.path
                      }
                      exact={childroute.exact}
                      children={childroute.component}
                    />
                  );
                })
              : []
          ];
        })}
      </Switch>
    </APIProvider>
  );
};

export default routes;
